import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
// import { ModalComponent } from '../shared/modal/modal.component';
import { AddContactComponent } from '../shared/modal/add-contact/add-contact.component';
import { FormGroup } from '@angular/forms';
import { FilterComponent } from '../shared/components/filter/filter.component';
import { GridColumn, ListComponent } from '../shared/components/list/list.component';
import { ButtonModule } from 'primeng/button';
import { ContactListService } from 'com.trumbull.phx.contactlist.api-client';
import { VocabHelperService } from '../services/vocab-helper/vocab-helper.service';
import { DialogModule } from 'primeng/dialog';
import { LoaderService } from '../services/loader-service/loader.service';
import { LoaderComponent } from '../loader/loader.component';
import { partial } from 'lodash';
import { ContactHelperService } from '../services/contact-helper/contact-helper.service';

export interface FilterField {
  type: 'select' | 'multiselect' | 'text';
  label: string;
  name: string;
  placeholder?: string;
  options: any[];
  value: any;
  optionLabel?: string;
  optionValue?: string;
  maxSelectedLabels?: number;
  class?: string;
  onChange?: () => void;
}

@Component({
  selector: 'app-client-manager-contacts',
  imports: [
    CommonModule, 
    ButtonModule,
    AddContactComponent, 
    FilterComponent, 
    ListComponent, 
    DialogModule,
    LoaderComponent
  ],
  templateUrl: './client-manager-contacts.component.html',
  styleUrl: './client-manager-contacts.component.scss'
})
export class ClientManagerContactsComponent implements OnInit {
  contactForm!: FormGroup;
  showAddContactModal: boolean = false;
  @ViewChild('addContactComp') addContactComponent!: AddContactComponent;
  // @ViewChild(AddContactComponent)
  addContactComp!: AddContactComponent;
  contacts: any[] = [];
  totalRecords = 0;
  loading = false;
  visible: boolean = false;
  titles: any[] = []; // Initialize as empty array
  accountNames: any[] = []; // Initialize as empty array
  statuses: any[] = []; // Initialize as empty array
  providerId: string = "standard";
  accountType: string = "customer";
  listId: string = "";
  contactList: any = [];

  contactTitleVocab: any;
  contactStatusVocab: any;
  accountNameVocab: any;
  isEditModeEnabled: boolean = false;
  addContactHeader: string = "Add Contact";
  originalContactList: any[] = [];

  tableParams = {
    first: 0,
    rowsPerPage: 10,
    sortField: 'name',
    sortOrder: 1
  };

  customLabels = {
    account: 'Client Name',
    name: 'Name',
    title: 'Title',
    email: 'Email',
    phone: 'Phone Number',
    extension: 'Extension',
    status: 'Status',
    barNumber: 'Bar Number'
  };

  columns: GridColumn[] = [
    { field: 'name', header: 'Name', isSortable: true },
    { field: 'title', header: 'Title', isSortable: true },
    { field: 'email', header: 'Email', isSortable: true },
    {
      field: 'phone',
      header: 'Phone & Extn',
      isSortable: true,
      customRender: (row) => `${row.phone}${row.extn ? ' Ext. ' + row.extn : ''}`
    },
    { field: 'clientName', header: 'Client Name', isSortable: true },
    { field: 'status', header: 'Status', isSortable: true }
  ];

  customVisibility = {
    name: true,
    phone: true,
    email: true,
    account: true,
    title: true,
    barNumber: false,  // hide
    extension: true,  // hide
    status: true      // hide
  };

  customPlaceholders = {
    account: 'Select client name',
    name: 'Enter name',
    title: 'Select title',
    extension: '--',
    status: 'Select status',
    phone: 'Enter phone',
    email: 'Enter email',
    barNumber: "Enter attorney's bar number"
  };

  ordersListFilter = {
    clientName: [],
    title: null,
    status: [],
    name: null,
  };

  filterFields: FilterField[] = [];
  panelCollapsed: boolean = false;

  constructor(
    private contactListService: ContactListService,
    private vocabHelperService: VocabHelperService,
    private loaderService: LoaderService,
    private readonly contactHelperService: ContactHelperService
  ) { }

  // Lifecycle Methods
  ngOnInit(): void {
    
    // Load vocabulary data first, then initialize filter fields
    this.getDataToInitialize();
    
    // Load contact data
    this.loadData();
    
    // Remove the manual initializeFilterFields call since it's now called in getDataToInitialize()
    // this.initializeFilterFields(); // Remove this line
  }

  onCancelForm() {
    this.isEditModeEnabled = false;
    this.addContactHeader = 'Add Contact';
    if (this.addContactComp && typeof this.addContactComp.onCancel === 'function') {
      this.addContactComp.onCancel();
    }    
    this.addContactComponent.resetForm();
    this.contacts = [...this.originalContactList];
    this.visible = false;
  }

  onOpenAddContactDialog(): void {
    this.onCancelForm();
    this.visible = true;
  }

  onDialogHide(): void {
    this.onCancelForm();
  }

  onAddContact() {
    this.addContactHeader = "Add Contact";
    this.isEditModeEnabled = false;
    this.visible = true;
  
    // Mark all fields as touched to show validation errors
    this.addContactComponent.markTouched();

    // Check if form is valid
    if (this.addContactComponent.isValid()) {
      // Get form data
      const contactData = this.addContactComponent.getFormData();

      // Process the data (save to service, API call, etc.)
      this.saveContact(contactData);

      // Close the dialog
      this.visible = false;

      // Reset the form for next use
      this.addContactComponent.resetForm();
    } else {
      console.warn('Form is invalid. Please check required fields.');
    }
  }

  onSaveAndAddContact() {
    this.onAddContact();
    // if (this.validateAndProcessContact()) {
    //   this.closeModalAndReset();
    // }
    this.visible = true;
  }

  private saveContact(contactData: any): void {
    this.listId = this.contactList.find((list: any) => list.name === contactData.accountName.code)?.id || "";
    // Transform data if needed
    const transformedData = {
      name: contactData.name,
      title: contactData.title,
      email: contactData.email,
      phone: contactData.phone,
      extn: contactData.extension,
      accountName: contactData.accountName,
      status: contactData.status,
      barNumber: contactData.barNumber
    };

    const party: any = this.getPartyCreated(contactData);
    
    this.contactListService.createContact(this.providerId, this.listId, party).subscribe({
      next: (response) => {
        this.loadData(); // Refresh the list
      },
      error: (error) => {
        console.error('Error saving contact:', error);
      }
    });
  }

  getFirstName(fullName: string): string {
    return this.contactHelperService.getFirstName(fullName);
  }

  getLastName(fullName: string): string {
    return this.contactHelperService.getLastName(fullName);
  }

  onSelectClientName() {
    const selectedField = this.filterFields.find(field => field.name === 'clientName');
    if (selectedField) {
      // this.ordersListFilter.accountName = selectedField.value;
    }
  }

  applyFilters(event: any): void {
    const filterValues = event; // Get the filter values from the event    
    const clientNames = this.getClientNames(filterValues.clientName);
    const contactName = filterValues.name;
    const status = this.getStatusCodes(filterValues.status);
    let title = this.getTitleValues(filterValues.title);
    this.listId = this.getListIdByClientNames(filterValues.clientName);
    
    if (!title) {
      title = this.titles.map(t => t.code).join(',');
    }

    // Call API with filter values
    this.contactListService.searchContacts(
                  this.providerId, // providerId
                  this.listId, // contactListId
                  contactName, // contactName
                  '', // accountNames
                  clientNames, // clientNames
                  status, // status
                  '', // partyid
                  title, // tagName
                  '', // contactId
                  'Person', // typeCode
                  '', // sort
                  undefined, // page
                  undefined // pageSize
              ).subscribe({
      next: (data: any) => {
        this.contacts = []; // Clear existing contacts before loading new data
        data.contactList.forEach((list: any) => {
          this.contactList.push({ id: list.id, name: list.name });
          // contactItems = list.contactItems;
          if (list.contactItems && list.contactItems.length) {
            this.mapApiDataToContacts(list.contactItems, list.name);

            // Apply sorting after data is mapped
            // if (this.tableParams.sortField) {
            //   this.applySorting(this.tableParams.sortField, this.tableParams.sortOrder);
            // }
          }
          // else {
          //   console.warn("No contact items found in response");
          //   this.contacts = [];
          // }
        });
        // this.listId = data.contactList[1].id;
        this.totalRecords = this.contacts.length;
        this.loading = false;
      },
      error: (error) => {
        console.error("Error fetching contacts:", error);
        this.contacts = [];
        this.totalRecords = 0;
        this.loading = false;
      }
    });
    // this.loadData();
  }

  getClientNames(selectedClients: any[]): string {
    // If there are selected client names in the filter, return only those
    let clientNames: string = "";
    if (selectedClients && selectedClients.length > 0) {
      selectedClients.forEach(clientCode => {
        clientNames === "" ? clientNames = clientCode : clientNames += "," + clientCode;

      });
    }
    return clientNames;
  }

  getStatusCodes(selectedStatuses: any[]): string {
    // If there are selected status in the filter, return only those
    let statuses: string = "";
    if (selectedStatuses && selectedStatuses.length > 0) {
      selectedStatuses.forEach(statusCode => {
        statuses === "" ? statuses = this.getStatus(statusCode) : statuses += "," + this.getStatus(statusCode);
      });
    }
    return statuses;
  }

  getStatus(code: string): string {
    if (code.toLowerCase() === 'active') {
      return 'true';
    } else if (code.toLowerCase() === 'inactive') {
      return 'false';
    }
    return code;
  }

  getTitleValues(selectedTitles: any[]): string {
    // If there are selected titles in the filter, return only those
    let titles: string = "";
    if (selectedTitles && selectedTitles.length > 0) {
      selectedTitles.forEach(titleCode => {
        titles === "" ? titles = titleCode : titles += "," + titleCode;
      });
    }
    return titles;
  }

  getListIdByClientNames(selectedClients: any[]): string {
    // If there are selected client names in the filter, return only those
    let listId: string = "";
    if (selectedClients && selectedClients.length > 0) {
      selectedClients.forEach(clientCode => {
        listId === "" ? listId = this.getListId(clientCode) : listId += "," + this.getListId(clientCode);
      });
    }
    return listId;
  }

  getListId(clientCode: string): string {
    return this.contactList.find((list: any) => list.name === clientCode)?.id || "";
  }

  resetFilters() {
    this.loadData();
    this.ordersListFilter = {
      clientName: [],
      title: null,
      status: [],
      name: null,
    };
    
    // Reset filter field values
    this.filterFields.forEach(field => {
      switch (field.name) {
        case 'clientName':
          field.value = [];
          break;
        case 'title':
          field.value = null;
          break;
        case 'status':
          field.value = [];
          break;
        case 'name':
          field.value = null;
          break;
      }
    });
  }

  loadData(event: any = null): void {
    this.loading = true;

    try {
      // Update table params if event is provided
      if (event) {
        this.updateTableParams(event);
      }

      this.getClientContactData(event); // Pass event to preserve sorting
    } catch (error) {
      console.error('Error loading data:', error);
      this.loading = false;
    } finally {
      // this.loaderService.hide();
    }
  }

  onPageChange(event: any) {
    this.loadData(event);
  }

  // onSortChange(event: any) {
  // console.log('Sort change event:', event);

  //   // Update table params with sort information
  //   this.tableParams.sortField = event.sortField;
  //   this.tableParams.sortOrder = event.sortOrder || 1; // Default to ascending if undefined
  // }

  private updateTableParams(event: any): void {
    this.tableParams = {
      first: event?.first || 0,
      rowsPerPage: event?.rows || 10,
      sortField: event?.sortField || 'name',
      sortOrder: event?.sortOrder || 1
    };
  }

  //Remove after integrating with backend
  // generateDummyData(count: number) {
  //   const titles = ['Attorney', 'Paralegal', 'Legal Assistant', 'Partner'];
  //   const statuses = ['Active', 'Inactive', 'Pending'];

  //   return Array.from({ length: count }).map((_, i) => ({
  //     name: `User ${i + 1}`,
  //     title: titles[i % titles.length],
  //     email: `user${i + 1}@lawfirm.com`,
  //     phone: `555-01${i.toString().padStart(2, '0')}`,
  //     extn: i % 3 === 0 ? 100 + i : '',
  //     accountName: `Account ${Math.ceil((i + 1) / 5)}`,
  //     status: statuses[i % statuses.length]
  //   }));
  // }

  getClientContactData(event: any = null) {
    this.loading = true;
    // let contactItems: any[] = [];
    this.loaderService.show();
    this.contactListService.listContactLists(this.providerId, "", "", this.accountType).subscribe({
      next: (data: any) => {
        this.loaderService.hide();
        this.contacts = []; // Clear existing contacts before loading new data
        data.contactList.forEach((list: any) => {
          this.contactList.push({ id: list.id, name: list.name });
          // contactItems = list.contactItems;
          if (list.contactItems && list.contactItems.length) {
            this.mapApiDataToContacts(list.contactItems, list.name);

            // Apply sorting after data is mapped
            // if (this.tableParams.sortField) {
            //   this.applySorting(this.tableParams.sortField, this.tableParams.sortOrder);
            // }
          }
          // else {
          //   console.warn("No contact items found in response");
          //   this.contacts = [];
          // }
        });
        // this.listId = data.contactList[1].id;



        this.totalRecords = this.contacts.length;
        this.loading = false;
      },
      error: (error) => {
        console.error("Error fetching contacts:", error);
        this.contacts = [];
        this.totalRecords = 0;
        this.loading = false;
      }
    });
  }

  mapApiDataToContacts(apiContacts: any, acctName: string) {
    // this.contacts = [];

    apiContacts.forEach((item: any) => {
      // Initialize variables for EACH contact (moved inside forEach)
      let id = item.id;
      let name = '';
      let title = '';
      let email = '';
      let clientName = this.capitalizeFirstLetter(acctName);
      let status = '';
      let phonePartyLocatorKeys: any[] = [];
      let emailPartyLocatorKeys: any[] = [];
      // let barNumber = '';

      const contactCore = this.contactHelperService.extractContactCore(item);

      // Extract basic info
      name = contactCore.displayName;
      status = contactCore.isActive ? 'Active' : 'Inactive';
      email = contactCore.email;
      phonePartyLocatorKeys = contactCore.phonePartyLocatorKeys;
      emailPartyLocatorKeys = contactCore.emailPartyLocatorKeys;
      title = contactCore.titleCode ? this.getTitleValue(contactCore.titleCode) : '';

      // Push the individual contact to the array
      this.contacts.push({
        id: id,
        name: name,
        title: title,
        email: email ? email : "--",
        phone: this.contactHelperService.formatPhoneDisplay(contactCore.workPhone.number, contactCore.workPhone.extension),
        // extn: extn,
        clientName: clientName,
        status: status,
        // barNumber: barNumber
        phonePartyLocatorKeys: phonePartyLocatorKeys,
        emailPartyLocatorKeys: emailPartyLocatorKeys
      });
    });
    
    this.originalContactList = [...this.contacts];
  }

  private capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  private getTitleValue(code: string): string {
    return this.contactTitleVocab.vocabularyItems.find((item: any) => item.code === code)?.description || code;
  }

  private getTitleCodeForEdit(titleValue: string): string {
    return this.titles.find((item: any) => item.value?.toLowerCase() === titleValue?.toLowerCase())?.code || titleValue;
  }

  getDataToInitialize(): void {
    this.vocabHelperService.getVocabData(['ClientContactTitle', 'RecordRetrievalCustomerCode', 'ContactStatusCode']).subscribe(
      data => {
        // Process ClientContactTitle
        this.contactTitleVocab = data.find(d => d.vocabularyName === 'ClientContactTitle');
        if (this.contactTitleVocab && this.contactTitleVocab.vocabularyItems) {
          this.titles = []; // Reset the array
          this.contactTitleVocab.vocabularyItems.forEach((item: any) => {
            this.titles.push({ code: item.code, value: item.description });
          });
          
          this.titles = this.titles.sort((a: any, b: any) => {
            const aIsOthers = a.value.toLowerCase() === 'other';
            const bIsOthers = b.value.toLowerCase() === 'other';
            
            if (aIsOthers && !bIsOthers) return 1;
            if (!aIsOthers && bIsOthers) return -1;
            
            return a.value.localeCompare(b.value);
          });
        }

        // Process RecordRetrievalCustomerCode
        this.accountNameVocab = data.find(d => d.vocabularyName === 'RecordRetrievalCustomerCode');
        if (this.accountNameVocab && this.accountNameVocab.vocabularyItems) {
          this.accountNames = []; // Reset the array
          this.accountNameVocab.vocabularyItems.forEach((item: any) => {
            this.accountNames.push({ code: item.code, value: item.description });
          });
        }

        // Process ContactStatusCode
        this.contactStatusVocab = data.find(d => d.vocabularyName === 'ContactStatusCode');
        if (this.contactStatusVocab && this.contactStatusVocab.vocabularyItems) {
          this.statuses = []; // Reset the array
          this.contactStatusVocab.vocabularyItems.forEach((item: any) => {
            this.statuses.push({ code: item.code, value: item.description });
          });
        }

        // Initialize filter fields after data is loaded
        this.initializeFilterFields();
        
      },
      error => {
        console.error('Error loading vocabulary data:', error);
      }
    );
  }

  private initializeFilterFields(): void {
    this.filterFields = [
      {
        type: 'multiselect',
        label: 'Client Name',
        name: 'clientName',
        placeholder: 'Select client name',
        options: this.accountNames, // This will be populated from getDataToInitialize()
        value: this.ordersListFilter.clientName,
        optionLabel: 'value', // Add this to specify the display property
        optionValue: 'code',  // Add this to specify the value property
        onChange: () => this.onSelectClientName()
      },
      {
        type: 'multiselect',
        label: 'Title',
        name: 'title',
        placeholder: 'Select title',
        options: this.titles, // Pass the titles array here
        value: this.ordersListFilter.title,
        optionLabel: 'value', // Add this to specify the display property
        optionValue: 'code'   // Add this to specify the value property
      },
      {
        type: 'multiselect',
        label: 'Status',
        name: 'status',
        placeholder: 'Select status',
        options: this.statuses, // This will be populated from getDataToInitialize()
        value: this.ordersListFilter.status,
        optionLabel: 'value', // Add this to specify the display property
        optionValue: 'code'   // Add this to specify the value property
      },
      {
        type: 'text',
        label: 'Name',
        name: 'name',
        placeholder: 'Enter name',
        options: [],
        value: this.ordersListFilter.name
      }
    ];
  }

  private refreshFilterOptions(): void {
    // Update the options in existing filter fields
    this.filterFields.forEach(field => {
      switch (field.name) {
        case 'title':
          field.options = this.titles;
          break;
        case 'clientName':
          field.options = this.accountNames;
          break;
        case 'status':
          field.options = this.statuses;
          break;
      }
    });
  }

    // Add this method to handle name clicks
  onContactNameClick(event: any): void {
    this.isEditModeEnabled = true;
    let eventData = event.data;
    eventData.id = event.data.id;
    eventData.clientName = { code: eventData.clientName.toLowerCase(), value: this.getAccountNameforEdit(eventData.clientName.toLowerCase()) };
    eventData.status = { code: eventData.status.toLowerCase(), value: eventData.status };
    eventData.title = { code: this.getTitleCodeForEdit(eventData.title), value: eventData.title };
    const phoneNo = eventData.phone;
    eventData.phone = phoneNo.split(' extn. ')[0];
    eventData.extension = phoneNo.split(' extn. ')[1];

    // eventData.forEach((item: any) => {
    //   item.key === 'accountName' ? item.value = this.getAccountCode(item.value) : null;
    //   item.key === 'status' ? item.value = item.value.toLowerCase() : null;
    //   item.key === 'title' ? item.value = item.value.toLowerCase() : null;
    // });
    const contactData = event.data;
    const rowIndex = event.index;
    
    this.editContact(contactData);
  }

  getAccountNameforEdit(code: string): string {
    return this.accountNames.find((item: any) => item.code === code)?.value || code;
  }

  // Edit contact method
  editContact(contactData: any): void {
    // Populate the add contact form with existing data for editing
    this.addContactHeader = 'Edit Contact';
    this.visible = true;
    
    // Wait for the component to be available
    setTimeout(() => {
      if (this.addContactComponent) {
        this.addContactComponent.setFormData(contactData);
        this.addContactComponent.isEditModeEnabled = true;
        
        // Disable account name field in edit mode
        this.addContactComponent.disableAccountNameField();
      }
    }, 100);

  }

  onUpdateContact(): void {
    // Mark all fields as touched to show validation errors
    this.addContactComponent.markTouched(); 

    // Check if form is valid
    if (this.addContactComponent.isValid()) {
      // Get form data
      const contactData = this.addContactComponent.getFormData();

      // Process the data (save to service, API call, etc.)
      this.updateContact(contactData);

      // Close the dialog
      this.visible = false;

      // Reset the form for next use
      this.addContactComponent.resetForm();
    } else {
      console.warn('Form is invalid. Please check required fields.');
    }
  }

  updateContact(contactData: any): void {
    this.listId = this.contactList.find((list: any) => list.name === contactData.accountName.code)?.id || "";
    
    const contactId = contactData.id;

    const party: any = this.getPartyCreated(contactData);

    this.contactListService.updateContact(this.providerId, this.listId, contactId, party).subscribe({
      next: (response) => {
        this.loadData(); // Refresh the list
      },
      error: (error) => {
        console.error('Error updating contact:', error);
      }
    });
    this.isEditModeEnabled = false;
    this.addContactHeader = 'Add Contact';
  }

  getPartyCreated(contactData: any): any {
    return this.contactHelperService.buildContactPartyPayload({
      displayName: contactData.name,
      titleCode: contactData.title.code,
      barNumber: contactData.barNumber,
      phone: contactData.phone,
      extension: contactData.extension,
      email: contactData.email,
      phonePartyLocatorKeys: this.getPhonePartyLocatorKeys(contactData.id),
      emailPartyLocatorKeys: this.getEmailPartyLocatorKeys(contactData.id),
      active: contactData.status.toLowerCase() === 'active' ? true : false
    });
  }

  getPhonePartyLocatorKeys(contactId: string): any[] {
    return this.originalContactList.find(contact => contact.id === contactId)?.phonePartyLocatorKeys || [];
  }

  getEmailPartyLocatorKeys(contactId: string): any[] {
    return this.originalContactList.find(contact => contact.id === contactId)?.emailPartyLocatorKeys || [];
  }

  // getContactId(contactName: string): string {
  //   const contact = this.originalContactList.find(c => c.name === contactName);
  //   return contact ? contact.id : '';
  // }

}

